import { useMemo, useEffect } from 'react';
import { useLazyQuery } from '@apollo/client';

import { notEmpty, matchMediaQuery } from '@crehana/utils';
import { useProductsContainer } from '@crehana/web/dist/ReactContexts/Products';

import getCoursesRecommended from 'Jsx/Catalog/graphql/searchCourses.graphql';
import type {
  CatalogSearchCourses,
  CatalogSearchCoursesVariables,
} from 'Jsx/Catalog/graphql/types/CatalogSearchCourses';

import {
  checkIfisInShoppingCart,
  COURSES_PAGE_SIZE,
  formatCourseV2,
} from '../utils';

function useCatalogSearchCourses(options?: {
  noResults?: boolean;
  minCourses?: number;
}) {
  const [getCourses, { data, loading, error }] = useLazyQuery<
    CatalogSearchCourses,
    CatalogSearchCoursesVariables
  >(getCoursesRecommended);

  useEffect(() => {
    const isDesktop = matchMediaQuery('sm').matches;

    getCourses({
      variables: {
        pageSize: isDesktop
          ? COURSES_PAGE_SIZE
          : options?.minCourses || COURSES_PAGE_SIZE,
        listRecommended: options?.noResults ? 'no-results' : undefined,
        newOrderBy: 'paid',
      },
    });
  }, []);

  const {
    shoppingCartState: { courseIdsInCart },
  } = useProductsContainer();

  const courses = useMemo(
    () =>
      data?.searchCourses?.edges
        .map(c => c?.node)
        .filter(notEmpty)
        .map(course => {
          const formatted = formatCourseV2(course);

          return checkIfisInShoppingCart(courseIdsInCart)(formatted);
        }) || [],
    [data, courseIdsInCart],
  );

  return { courses, loading, error };
}

export default useCatalogSearchCourses;
